import Immutable from 'seamless-immutable';
import flatten from 'ramda/src/flatten';
import pluck from 'ramda/src/pluck';
import pathOr from 'ramda/src/pathOr';
import { createSelector } from 'reselect';
import LocaleKeys from '../../locales/keys';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeDictionarySelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { CompletedOfferingAdditionalPropertyFieldsSelector, OrderAdditionalPropertyFieldsSelector } from 'invision-core/src/components/metadata/codes/codes.additional.properties.selectors';
import { ShippingMethodsSelector } from 'invision-core/src/components/metadata/shippingMethods/shipping.methods.selectors';
import { ALL_STEPS_KEYS, TransitionStates } from '../constants/transition.offer.wizard.constants';
import { AvailableCreditFromQuote, Cart, DeliveryMethodOptionsSelector, DeliveryOptionsByPricingPlanHelper, EditCart, EditOption, EditOptions, EditSelectedFacetIds, EditSelectedOfferId, FinanceCreditFromQuoteSelector, FormattedAttributes, FormattedDeliveryDecisions, FormattedPhysicalAttributes, FormAttributeGroupsHelper, FormPhysicalAttributeGroupsHelper, GetAdditionalPropertyListMapSelector, GetCartAdditionalPropertiesSelector, GetOfferingOrderQuoteSelector, GetOfferQuoteSelector, GetOrderAdditionalPropertiesSelector, GetPostQuoteDueTotalsSelector, GetPreQuoteDueSubTotalsSelector, GetQuoteHasInvoiceItemsSelector, GetTotalDownPaymentAmountSelector, IsCreditLimitBreachedFromQuote, IsSendToStoreButtonEnabledSelectorCreator, OfferOptionViewModel, PaymentInfo, SelectedEditOption, SelectedOfferId, ServiceAddress, ShippingAddressWizardSelector, SubscriberProductIdUndefinedSelector, SummaryTab, ShippingAddressDropdownOptionsWizardSelector, TaxLocationDecisionsByPricingPlanHelper } from '../helpers/offer.ordering.wizard.selector.helper';
import { BackButtonText, CompletedSteps, CurrentStep, CurrentStepIndex, CurrentStepIsInvalid, FormattedAddressCityState, FormattedName, ForwardButtonText, IsActiveTransitionStep, SelectedTabIndex, SkippedStep, StepProgressBarViewModel, TabPanelViewModel } from '../helpers/wizard.selector.helper';
import { ShoppingCartSelector, SelectedOfferingPhysicalInventoriesSelector } from './selected.offering.order.selectors';
import { CurrentCustomerCurrencyCodeSelector, CurrentCustomerSelector, RouteParams } from '../../reducers/selectors/customer.selectors';
import { IsQuoteBypassedSelector, OfferingOrderAsyncIdSelector, OfferingOrderQuoteSelector, OffersSelector, QuoteCalculatedSelector, OfferingOrderFinanceCreditLimitSelector, SelectedInventoryStoresSelector, SubTotalAmountSelector, TaxAmountSelector, TotalAmountSelector, TotalRemainingAmountSelector } from './offering.order.selectors';
import { SelectedContractSelector } from './contract.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { EMPTY_ARRAY } from '../constants/common.constants';
import { ShippingAddressesSelector } from './customer.addresses.selectors';
var OfferingOrderSelector = function OfferingOrderSelector(state) {
  return state.customercare.offeringOrder;
};
var TransitionOfferWizardStoreSelector = function TransitionOfferWizardStoreSelector(state) {
  return state.customercare.recoverableUiState.transitionOfferWizard;
};
var DEFAULT_CUSTOMER = {};
export var CustomerInfoSelector = createSelector([CurrentCustomerSelector], function (customer) {
  if (customer) {
    return customer;
  } else {
    return DEFAULT_CUSTOMER;
  }
});
var CartSelector = Cart([TransitionOfferWizardStoreSelector]);
export var CurrentStepIndexSelector = CurrentStepIndex([TransitionOfferWizardStoreSelector]);
export var CurrentStepIsInvalidSelector = CurrentStepIsInvalid([TransitionOfferWizardStoreSelector]);
export var CurrentStepSelector = CurrentStep([CurrentStepIndexSelector], ALL_STEPS_KEYS);
export var IsCheckoutStepSelector = IsActiveTransitionStep([CurrentStepSelector], LocaleKeys.WIZARD.STEPS.CHECKOUT);
export var IsOffersStepSelector = IsActiveTransitionStep([CurrentStepSelector], LocaleKeys.WIZARD.STEPS.OFFERS);
export var IsCompareOffersStepSelector = IsActiveTransitionStep([CurrentStepSelector], LocaleKeys.WIZARD.STEPS.COMPARE_OFFERS);
export var IsDecisionsStepSelector = IsActiveTransitionStep([CurrentStepSelector], LocaleKeys.WIZARD.STEPS.DECISIONS);
export var IsAttributesStepSelector = IsActiveTransitionStep([CurrentStepSelector], LocaleKeys.WIZARD.STEPS.ATTRIBUTES);
export var CompletedStepsSelector = CompletedSteps([TransitionOfferWizardStoreSelector]);
export var EditCartSelector = EditCart([TransitionOfferWizardStoreSelector]);
export var SelectedEditOptionSelector = SelectedEditOption([EditCartSelector]);
export var EditSelectedOfferIdSelector = EditSelectedOfferId([EditCartSelector]);
export var EditSelectedFacetIdsSelector = EditSelectedFacetIds([EditCartSelector]);
export var EditOptionsSelector = EditOptions([EditCartSelector]);
export var EditOptionSelector = EditOption([SelectedEditOptionSelector, EditOptionsSelector]);
export var ServiceAddressSelector = ServiceAddress([TransitionOfferWizardStoreSelector]);
export var FormattedCityStateSelector = FormattedAddressCityState([ServiceAddressSelector]);
export var FormattedNameSelector = FormattedName([CustomerInfoSelector]);
export var SkippedStepSelector = SkippedStep([TransitionOfferWizardStoreSelector]);
export var SelectedOfferIdSelector = SelectedOfferId([CartSelector]);
export var FormattedAttributesSelector = FormattedAttributes([EditCartSelector]);
export var FormattedPhysicalAttributesSelector = FormattedPhysicalAttributes([EditCartSelector, SelectedOfferIdSelector, SelectedInventoryStoresSelector]);
export var IsSubscriberProductIdUndefinedSelector = SubscriberProductIdUndefinedSelector([FormattedPhysicalAttributesSelector]);
export var SelectedTabIndexSelector = SelectedTabIndex([TransitionOfferWizardStoreSelector]);
export var IsSummaryTabVisibleSelector = createSelector([SelectedTabIndexSelector], function (selectedTabIndex) {
  return selectedTabIndex === 0;
});
export var TabPanelViewModelSelector = TabPanelViewModel([SelectedTabIndexSelector]);
export var PaymentInfoSelector = PaymentInfo([CartSelector]);
export var PaymentInfoEditSelector = PaymentInfo([EditCartSelector]);
export var OfferQuoteSelector = GetOfferingOrderQuoteSelector([OfferingOrderQuoteSelector]);
export var TransitionOfferQuoteSelector = GetOfferQuoteSelector([TransitionOfferWizardStoreSelector]);
export var OfferQuoteFinanceCreditLimitSelector = OfferingOrderFinanceCreditLimitSelector;
export var AvailableFinanceCreditFromQuoteSelector = FinanceCreditFromQuoteSelector([OfferingOrderFinanceCreditLimitSelector]);
export var AvailableCreditFromQuoteSelector = AvailableCreditFromQuote([OfferQuoteSelector]);
export var IsCreditLimitBreachedFromQuoteSelector = IsCreditLimitBreachedFromQuote([OfferQuoteSelector]);
export var TotalDownPaymentSelector = GetTotalDownPaymentAmountSelector([TransitionOfferQuoteSelector]);
export var PostQuoteDueTotalsSelector = GetPostQuoteDueTotalsSelector([OfferQuoteSelector]);
export var PreQuoteDueSubTotalsSelector = GetPreQuoteDueSubTotalsSelector([ShoppingCartSelector]);
export var QuoteHasInvoiceItemsSelector = GetQuoteHasInvoiceItemsSelector([OfferQuoteSelector]);
export var ServiceTaxDecisionsSelector = createImmutableSelector([EditCartSelector], function (editCart) {
  return editCart.serviceTaxDecisions || EMPTY_ARRAY;
});
export var SummaryTabViewModelSelector = SummaryTab([ShoppingCartSelector, SubTotalAmountSelector, TaxAmountSelector, TotalAmountSelector, QuoteCalculatedSelector, SelectedContractSelector, TotalRemainingAmountSelector, function () {
  return undefined;
}, TotalDownPaymentSelector, PostQuoteDueTotalsSelector, PreQuoteDueSubTotalsSelector]);
export var TransitionIsAllowedSelector = createSelector([MetadataCodeTypeDictionarySelector(CODES.TransitionConfiguration)], function (transitionConfiguration) {
  return pathOr(false, [Object.keys(transitionConfiguration)[0], 'AdditionalProperties', 'AllowTransitioning'], transitionConfiguration) === 'True';
});
var OfferingOrderDataSelector = createSelector([OfferingOrderSelector], function (offeringOrder) {
  return offeringOrder.data;
});
export var SelectedOfferingResponseIdSelector = createSelector([OfferingOrderDataSelector], function (offeringOrderData) {
  return offeringOrderData.selectedOffering.data.rocResponseOfferId;
});
var AvailableOfferingsDataSelector = createSelector([OfferingOrderDataSelector], function (orderStoreData) {
  return orderStoreData.availableOfferings.data;
});
export var BackButtonTextSelector = BackButtonText([CurrentStepIndexSelector, CompletedStepsSelector], ALL_STEPS_KEYS);
export var EditSelectedOfferingChargeTypeIdSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.selectedOfferingChargeTypeIds;
});
export var ForwardButtonTextSelector = ForwardButtonText([CurrentStepIndexSelector, SkippedStepSelector], ALL_STEPS_KEYS);
export var OfferingsSelector = createSelector([AvailableOfferingsDataSelector], function (availableOfferingsData) {
  return availableOfferingsData.offeringsMap;
});
export var OfferOptionViewModelSelector = OfferOptionViewModel([OffersSelector, EditSelectedOfferIdSelector]);
export var StepProgressBarViewModelSelector = StepProgressBarViewModel([CurrentStepSelector, CompletedStepsSelector, SkippedStepSelector], ALL_STEPS_KEYS);
export var TransitionContextSelector = createSelector([TransitionOfferWizardStoreSelector], function (transitionStore) {
  return transitionStore.transitionContext;
});
export var TransitionOfferingsMetadataSelector = createImmutableSelector([TransitionOfferWizardStoreSelector], function (transitionStore) {
  return transitionStore.offeringsMetadata;
});
export var TransitionCurrentOfferingsSelector = createSelector([TransitionContextSelector, TransitionOfferingsMetadataSelector], function (transitionContext, offeringsMetadata) {
  return transitionContext && transitionContext.CurrentOfferings ? transitionContext.CurrentOfferings.map(function (currentOffer) {
    var offer = offeringsMetadata.find(function (offeringMetadata) {
      return offeringMetadata.Id === currentOffer.Id;
    });
    return Object.assign({}, currentOffer, {
      DisplayName: offer ? offer.DisplayName : undefined,
      Name: offer ? offer.Name : currentOffer.Name
    });
  }) : [];
});
export var TransitionOfferingsSelector = createSelector([TransitionContextSelector, TransitionOfferingsMetadataSelector], function (transitionContext, offeringsMetadata) {
  return transitionContext && transitionContext.TransitionOfferings ? transitionContext.TransitionOfferings.map(function (currentOffer) {
    var offer = offeringsMetadata.find(function (offeringMetadata) {
      return offeringMetadata.Id === currentOffer.Id;
    });
    return Object.assign({}, currentOffer, {
      DisplayName: offer ? offer.DisplayName : undefined,
      Name: offer ? offer.Name : currentOffer.Name
    });
  }) : [];
});
export var TransitionOutcomesSelector = createSelector([TransitionContextSelector], function (transitionContext) {
  return transitionContext && transitionContext.Outcomes || {};
});
export var TransitionParsedOutcomesSelector = createSelector([TransitionOutcomesSelector, TransitionCurrentOfferingsSelector, TransitionOfferingsSelector], function (outcomes, currentOfferings, transitionOfferings) {
  var emptyOutcomes = !Object.keys(outcomes).length && outcomes.constructor === Object;
  return !emptyOutcomes ? outcomes.asMutable({
    deep: true
  }).map(function (outcome) {
    var currentAmount = getOutcomeAmount(outcome, currentOfferings),
      transitionAmount = getOutcomeAmount(outcome, transitionOfferings);
    var remainingbalance = getOutcomeRemainingBalance(outcome, currentOfferings);
    var transitionRemainingBalance = getOutcomeRemainingBalance(outcome, transitionOfferings);
    var isAddition = outcome.TransitionState === TransitionStates.ADDED,
      isRemoval = outcome.TransitionState === TransitionStates.REMOVED,
      isChange = outcome.TransitionState === TransitionStates.CHANGED && !!currentAmount && !!transitionAmount && currentAmount !== transitionAmount;
    return {
      current: {
        title: !isAddition ? getOutcomeTitle(outcome, currentOfferings) : '',
        discountedPrice: !isAddition ? getOutcomeDiscount(outcome, currentOfferings) : null,
        updatedPrice: !isAddition ? getUpdatedPrice(outcome, currentOfferings) : null,
        price: !isAddition ? currentAmount : null,
        remainingbalance: !isAddition ? remainingbalance : null,
        termAmount: !isAddition ? getTermAmount(outcome, currentOfferings) : null
      },
      transition: {
        title: !isRemoval ? getOutcomeTitle(outcome, transitionOfferings) : '',
        discountedPrice: !isRemoval ? getOutcomeDiscount(outcome, transitionOfferings) : null,
        updatedPrice: !isRemoval ? getUpdatedPrice(outcome, transitionOfferings) : null,
        price: !isRemoval ? transitionAmount : null,
        remainingbalance: !isAddition ? transitionRemainingBalance : null,
        termAmount: !isRemoval ? getTermAmount(outcome, transitionOfferings) : null
      },
      isOrphaned: outcome.PendingOfferingSeparation,
      isAddition: isAddition,
      isRemoval: isRemoval,
      isChange: isChange
    };
  }) : [];
});
export var TransitionCurrentOffersSavingsSelector = createSelector([TransitionParsedOutcomesSelector], function (parsedOutcomes) {
  var currentOfferSavingsTotal = 0;
  if (parsedOutcomes && parsedOutcomes.length > 0) {
    parsedOutcomes.forEach(function (outcome) {
      currentOfferSavingsTotal += outcome.current.discountedPrice;
    });
  }
  return currentOfferSavingsTotal;
});
export var TransitionOffersSavingsSelector = createSelector([TransitionParsedOutcomesSelector], function (parsedOutcomes) {
  var transitionOfferSavingsTotal = 0;
  if (parsedOutcomes && parsedOutcomes.length > 0) {
    parsedOutcomes.forEach(function (outcome) {
      transitionOfferSavingsTotal += outcome.transition.discountedPrice;
    });
  }
  return transitionOfferSavingsTotal;
});
var getOutcomeDetailsFromOfferingItems = function getOutcomeDetailsFromOfferingItems(outcome, offerings) {
  var items = flatten(pluck('Items', offerings));
  return items.find(function (item) {
    return item.ProductId === outcome.ProductId && item.PricingPlanId === outcome.PricingPlanId;
  });
};
var getOutcomeTitle = function getOutcomeTitle(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? "".concat(outcomeDetails.ProductName, ": ").concat(outcomeDetails.PlanName) : '';
};
var getOutcomeAmount = function getOutcomeAmount(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? outcomeDetails.Amount : null;
};
var getOutcomeRemainingBalance = function getOutcomeRemainingBalance(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? outcomeDetails.RemainingBalance : null;
};
var getOutcomeDiscount = function getOutcomeDiscount(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? outcomeDetails.Discount : null;
};

///TODO: Remove Calculation logic from UI and obtain value from API results with UI story : ASC-99221
/// API Story https://jira.csgicorp.com/browse/ASC-99219
var getUpdatedPrice = function getUpdatedPrice(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? outcomeDetails.Amount - outcomeDetails.Discount : null;
};
var getTermAmount = function getTermAmount(outcome, items) {
  var outcomeDetails = getOutcomeDetailsFromOfferingItems(outcome, items);
  return outcomeDetails ? outcomeDetails.TermAmount : null;
};
var EMPTY_ATTRIBUTE_GROUPS = Immutable([]);
export var EditAttributeGroupsSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.editAttributeGroups ? editCart.editAttributeGroups.asMutable({
    deep: true
  }) : EMPTY_ATTRIBUTE_GROUPS;
});
export var DeliveryDecisionsSelector = createImmutableSelector([EditCartSelector], function (editCart) {
  return editCart.deliveryDecisions || EMPTY_ARRAY;
});
export var DeliveryOptionsByPricingPlanSelector = DeliveryOptionsByPricingPlanHelper([DeliveryDecisionsSelector, ShippingMethodsSelector]);
export var FormattedDeliveryDecisionsSelector = FormattedDeliveryDecisions([DeliveryDecisionsSelector]);
export var EditPhysicalAttributeGroupsSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.editPhysicalAttributeGroups ? editCart.editPhysicalAttributeGroups.asMutable({
    deep: true
  }) : EMPTY_ATTRIBUTE_GROUPS;
});
//Passing serviceAttribute for Edit and Change offer
//https://jira.csgicorp.com/browse/ASCBR-1369
export var FormAttributeGroups = FormAttributeGroupsHelper([EditAttributeGroupsSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute)]);
export var FormPhysicalAttributeGroups = FormPhysicalAttributeGroupsHelper([EditPhysicalAttributeGroupsSelector]);
export var TransitionLastAttemptErrorSelector = createSelector([TransitionOfferWizardStoreSelector], function (transitionStore) {
  return transitionStore.lastAttemptError;
});
export var TransitionOfferSubmittedCOSSelector = createSelector([TransitionOfferWizardStoreSelector], function (transitionStore) {
  return transitionStore.submittedCOS.Order;
});
export var IsSubmittingCOSOrderSelector = createSelector([TransitionOfferWizardStoreSelector], function (transitionStore) {
  return transitionStore.isSubmittingCOSOrder;
});
export var IsFetchingCurrentOfferSelector = createSelector([TransitionOfferWizardStoreSelector], function (transitionOfferWizardStor) {
  return Immutable(transitionOfferWizardStor.isFetchingCurrentOffer);
});
export var TransitionOfferingIdSelector = createSelector([RouteParams], function (routeParams) {
  return Immutable(routeParams.offeringId);
});
export var TransitionOfferingInstanceIdSelector = createSelector([RouteParams], function (routeParams) {
  return Immutable(routeParams.offeringInstanceId);
});
export var IsSendToStoreButtonEnabledSelector = IsSendToStoreButtonEnabledSelectorCreator([SelectedOfferingPhysicalInventoriesSelector, IsAttributesStepSelector, IsCheckoutStepSelector, OfferingOrderAsyncIdSelector]);
export var IsResumingStoreOrderSelector = createImmutableSelector([TransitionOfferWizardStoreSelector], function (changeOfferWizardStore) {
  return changeOfferWizardStore.isResumingStoreOrder;
});
export var IsShippingRequiredSelector = createSelector([ShoppingCartSelector], function (shoppingCart) {
  return pathOr(false, ['ShippingRequired'], shoppingCart);
});
export var CartAdditionalPropertiesSelector = GetCartAdditionalPropertiesSelector([TransitionOfferWizardStoreSelector]);
export var CartAdditionalPropertyListMapSelector = GetAdditionalPropertyListMapSelector([CartAdditionalPropertiesSelector, CompletedOfferingAdditionalPropertyFieldsSelector]);
export var OrderAdditionalPropertiesSelector = GetOrderAdditionalPropertiesSelector([TransitionOfferWizardStoreSelector]);
export var OrderAdditionalPropertyListMapSelector = GetAdditionalPropertyListMapSelector([OrderAdditionalPropertiesSelector, OrderAdditionalPropertyFieldsSelector]);
export var ShippingAddressSelector = ShippingAddressWizardSelector([IsQuoteBypassedSelector, OfferingOrderDataSelector, ShippingAddressesSelector]);
export var ShippingAddressDropdownOptionsSelector = ShippingAddressDropdownOptionsWizardSelector([OfferingOrderDataSelector, ShippingAddressesSelector]);
export var TransitionOfferDeliveryMethodOptionsSelector = DeliveryMethodOptionsSelector([ShippingMethodsSelector, CurrentCustomerCurrencyCodeSelector]);
export var TaxLocationDecisionsByPricingPlanChangeOfferSelector = TaxLocationDecisionsByPricingPlanHelper([ServiceTaxDecisionsSelector]);